import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { useLanguage } from "@/context/LanguageContext";
import { Users, BookOpen, ClipboardList, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState({
    users: 0,
    activeUsers: 0,
    exams: 0,
    templates: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [usersRes, examsRes, aTemplatesRes, cTemplatesRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/exams`),
        axios.get(`${API}/admin/anamnesis-templates`),
        axios.get(`${API}/admin/consent-templates`),
      ]);
      
      setStats({
        users: usersRes.data.length,
        activeUsers: usersRes.data.filter(u => u.is_active).length,
        exams: examsRes.data.length,
        templates: aTemplatesRes.data.length + cTemplatesRes.data.length,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-12" data-testid="admin-dashboard">
        {/* Header */}
        <div className="mb-12">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t("admin_panel")}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl">
            {t("overview")}
          </h1>
        </div>

        {/* Stats */}
        <div className="bento-grid mb-12">
          <Link to="/admin/users" className="bento-item" data-testid="stat-users">
            <Users size={24} strokeWidth={1} className="mb-4 text-muted-foreground" />
            <p className="stat-value">{loading ? "-" : stats.users}</p>
            <p className="stat-label">{t("registered_users")}</p>
            <p className="text-sm text-muted-foreground mt-2">
              {loading ? "-" : stats.activeUsers} {t("active")}
            </p>
          </Link>

          <Link to="/admin/exams" className="bento-item" data-testid="stat-exams">
            <BookOpen size={24} strokeWidth={1} className="mb-4 text-muted-foreground" />
            <p className="stat-value">{loading ? "-" : stats.exams}</p>
            <p className="stat-label">{t("created_exams")}</p>
          </Link>

          <Link to="/admin/templates" className="bento-item" data-testid="stat-templates">
            <ClipboardList size={24} strokeWidth={1} className="mb-4 text-muted-foreground" />
            <p className="stat-value">{loading ? "-" : stats.templates}</p>
            <p className="stat-label">{t("templates")}</p>
          </Link>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="font-serif text-xl mb-4">{t("quick_actions")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/admin/users" className="bento-item text-center">
              <Users size={32} strokeWidth={1} className="mx-auto mb-2" />
              <p className="font-mono text-xs uppercase tracking-widest">{t("manage_users")}</p>
            </Link>
            <Link to="/admin/exams/new" className="bento-item text-center">
              <BookOpen size={32} strokeWidth={1} className="mx-auto mb-2" />
              <p className="font-mono text-xs uppercase tracking-widest">{t("new_exam")}</p>
            </Link>
            <Link to="/admin/templates" className="bento-item text-center">
              <ClipboardList size={32} strokeWidth={1} className="mx-auto mb-2" />
              <p className="font-mono text-xs uppercase tracking-widest">{t("edit_templates")}</p>
            </Link>
            <Link to="/admin/exams" className="bento-item text-center">
              <TrendingUp size={32} strokeWidth={1} className="mx-auto mb-2" />
              <p className="font-mono text-xs uppercase tracking-widest">{t("see_exams_admin")}</p>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminDashboard;
